/**
 * `FadeRiseIn` — the subtle text entrance (motion ronda 14): a small
 * opacity + translateY(4px) rise over `MOTION.durBase`, driven by
 * `useEntrance()` (which already carries the `delayEntrance` beat and
 * the hidden/occluded-document guards).
 *
 * Used for KPI display values whose pre-formatted string does NOT
 * parse to a number (`parseFormattedNumber` → null): the string is
 * rendered as-is — nothing derived, nothing invented — and only its
 * appearance animates. Compositor-only (opacity/transform); reduced
 * motion renders the final state immediately via `useEntrance`.
 */
import type { ReactNode } from "react";

import {
  MOTION,
  useEntrance,
  usePrefersReducedMotion,
} from "@pervasivemind/bi-components";

export interface FadeRiseInProps {
  children: ReactNode;
  className?: string;
}

export function FadeRiseIn({ children, className }: FadeRiseInProps): JSX.Element {
  const entered = useEntrance();
  const reduced = usePrefersReducedMotion();
  return (
    <span
      className={className}
      data-pm-fade-rise
      style={{
        display: "inline-block",
        opacity: entered ? 1 : 0,
        transform: entered ? "none" : "translateY(4px)",
        transition: reduced
          ? undefined
          : `opacity ${MOTION.durBase}ms ${MOTION.easeOut}, transform ${MOTION.durBase}ms ${MOTION.easeOut}`,
      }}
    >
      {children}
    </span>
  );
}
